import { ReactNode } from 'react';

import { createJapaneseSearchRegex } from './japaneseCharacterUtils';

/**
 * Highlights matching text in a string based on a search keyword
 * @param text - The text to search within
 * @param keyword - The keyword to highlight
 * @param className - Optional CSS class for highlighted text
 * @returns JSX element with highlighted text
 */
export function highlightText(
  text: string,
  keyword: string,
  className: string = 'bg-[#F9F7D1]',
): ReactNode {
  if (!keyword || !text || keyword.trim() === '') {
    return text;
  }

  const regex = createJapaneseSearchRegex(keyword.trim());
  const parts = text.split(regex);

  return (
    <>
      {parts.map((part, index) => {
        if (regex.test(part)) {
          return (
            <span
              key={index}
              className={className}
            >
              {part}
            </span>
          );
        }
        return <span key={index}>{part}</span>;
      })}
    </>
  );
}

/**
 * Highlights matching text in filename
 * @param filename - The filename to highlight
 * @param keyword - The keyword to highlight
 * @returns JSX element with highlighted filename
 */
export function highlightFilename(filename: string, keyword: string): ReactNode {
  return highlightText(filename, keyword, 'bg-[#F9F7D1]');
}

/**
 * Highlights matching text and returns HTML string for dangerouslySetInnerHTML
 * This version is HTML-aware and preserves existing HTML tags like mentions
 * @param html - The HTML content to search within
 * @param keyword - The keyword to highlight
 * @param className - Optional CSS class for highlighted text
 * @returns HTML string with highlighted text, preserving existing HTML structure
 */
export function highlightTextAsHTML(
  html: string,
  keyword: string,
  className: string = 'bg-[#F9F7D1]',
): string {
  if (!keyword || !html || keyword.trim() === '') {
    return html;
  }

  // Check if we're in browser environment
  if (typeof DOMParser === 'undefined' || typeof document === 'undefined') {
    return highlightTextAsHTMLFallback(html, keyword, className);
  }

  const regex = createJapaneseSearchRegex(keyword.trim());

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
    const container = doc.body.firstChild as HTMLElement;

    if (!container) {
      return html;
    }

    function highlightTextNodes(node: Node): void {
      if (node.nodeType === Node.TEXT_NODE) {
        const textContent = node.textContent || '';

        if (!regex.test(textContent)) {
          return;
        }

        const highlightedText = textContent.replace(regex, `<span class="${className}">$1</span>`);

        const wrapper = document.createElement('div');
        wrapper.innerHTML = highlightedText;

        const parent = node.parentNode;
        if (!parent) {
          return;
        }

        const fragment = document.createDocumentFragment();
        while (wrapper.firstChild) {
          fragment.appendChild(wrapper.firstChild);
        }
        parent.replaceChild(fragment, node);
        return;
      }

      if (node.nodeType !== Node.ELEMENT_NODE) {
        return;
      }

      const element = node as HTMLElement;

      // Handle mention elements specially - highlight text content but preserve mention structure
      if (element.tagName === 'A' && element.classList.contains('mention')) {
        const textContent = element.textContent || '';
        if (regex.test(textContent)) {
          const highlightedText = textContent.replace(
            regex,
            `<span class="${className}">$1</span>`,
          );

          if (highlightedText !== textContent) {
            element.innerHTML = highlightedText;
          }
        }
        return;
      }

      const children = Array.from(node.childNodes);
      children.forEach((child) => highlightTextNodes(child));
    }

    highlightTextNodes(container);
    return container.innerHTML;
  } catch (error) {
    console.warn('DOM parsing failed, falling back to simple highlighting:', error);
    return highlightTextAsHTMLFallback(html, keyword, className);
  }
}

/**
 * Fallback highlighting function for server-side rendering or when DOM parsing fails
 */
function highlightTextAsHTMLFallback(html: string, keyword: string, className: string): string {
  const regex = createJapaneseSearchRegex(keyword.trim());

  // Handle mentions specially to preserve their structure
  const mentionRegex = /<a[^>]*class=["'][^"']*mention[^"']*["'][^>]*>(.*?)<\/a>/gi;

  return html
    .replace(mentionRegex, (match, innerText) => {
      const highlightedInnerText = innerText.replace(regex, `<span class="${className}">$1</span>`);
      return match.replace(innerText, highlightedInnerText);
    })
    .replace(regex, (match) => {
      if (html.includes(`<a`) && html.includes(`class=`) && html.includes(`mention`)) {
        const beforeMatch = html.substring(0, html.indexOf(match));
        const openMentionCount = (
          beforeMatch.match(/<a[^>]*class=["'][^"']*mention[^"']*["'][^>]*/g) || []
        ).length;
        const closeMentionCount = (beforeMatch.match(/<\/a>/g) || []).length;

        if (openMentionCount > closeMentionCount) {
          return match;
        }
      }
      return `<span class="${className}">${match}</span>`;
    });
}
